// Utilities
import { defineStore } from "pinia";
import { useLocalStorage, useSessionStorage } from "@vueuse/core";
import { useApp } from "@/application/composables/app";
import { AppIdentifier } from "@/application/enums";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/application/stores/authStore";
import postRedirect from "@/application/utils/postRedirect";

export const useAppStore = defineStore("app", () => {
  const { appIdentifier, appData } = useApp();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const token = useLocalStorage<string | null>("token", null);

  const redirectUrl = useLocalStorage<string | null>("redirectUrl", null);
  const organisationUrl = useSessionStorage<string | null>(
    "organisationUrl",
    null,
  );

  const setCurrentLocation = () => {
    const hostname = window.location.hostname;

    // Keep organisation url, when not already in central
    if (appIdentifier !== AppIdentifier.CentralAdmin) {
      organisationUrl.value = hostname;
    }

    const pathname = window.location?.pathname;
    const isToAuth = pathname?.includes("/auth/");

    if (pathname && pathname !== "/" && !isToAuth) {
      redirectUrl.value = pathname;
    }
  };

  const redirectUserToLogin = () => {
    if (authStore.isLoggedIn) throw new Error("User is already logged in");

    if (appIdentifier === AppIdentifier.CentralAdmin) {
      if (route.name !== "auth.login") router.push({ name: "auth.login" });
      return;
    }

    const url = new URL(
      `${import.meta.env.VITE_CENTRAL_ADMIN_BASE_URL}/auth/callback`,
    );

    if (organisationUrl.value)
      url.searchParams.append("organisation_url", organisationUrl.value);

    window.location.assign(url);
  };

  // user needs to be logged in
  const redirectLoggedInUserToLocation = async ({
    redirectUrl,
    organisationUrl,
  }: {
    redirectUrl: string | null;
    organisationUrl: string | null;
  }) => {
    if (!authStore.profile) throw new Error("User is logged out");

    // not yet on correct platform
    if (
      !authStore.profile.role ||
      (organisationUrl && window.location.hostname !== organisationUrl)
    ) {
      await postRedirect("auth/authorize", {
        bearer_token: token.value as string,
        ...(organisationUrl ? { organisation_url: organisationUrl } : {}),
      });
      return;
    }

    // already on correct platform
    await router.replace(
      redirectUrl ? { path: redirectUrl } : appData.landingPage || "/",
    );
  };

  return {
    token,
    redirectUrl,
    organisationUrl,
    setCurrentLocation,
    redirectUserToLogin,
    redirectLoggedInUserToLocation,
  };
});
